import { useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AutoMatchProvider } from "@/contexts/AutoMatchContext";
import { WorkstationLayout } from "@/components/workstation/WorkstationLayout";
import { IncomingScanView } from "@/components/workstation/views/IncomingScanView";
import { InvoiceMatchingView } from "@/components/workstation/views/InvoiceMatchingView";
import { SubmissionConsole } from "@/components/workstation/views/SubmissionConsole";
import { useWorkstationStore, Product } from "@/hooks/useWorkstationStore";
import { useOptimizedProductList } from "@/hooks/useOptimizedProductList";
import { AlertCircle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

// Helper function to calculate data completeness
const calculateDataQuality = (product: any): 'high' | 'medium' | 'low' => {
  let score = 0;
  const factors = [
    product.name,
    product.barcode,
    product.costPerUnit,
    product.categoryId || product.categoryName,
    product.posStatus === 'found',
    product.photos?.length > 0
  ];
  
  score = factors.filter(Boolean).length;
  
  if (score >= 5) return 'high';
  if (score >= 3) return 'medium';
  return 'low';
};

const Workstation = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    setOrderId,
    setProducts,
    setCategories,
    setInvoiceItems,
    activeView,
    setActiveView,
    setLoading
  } = useWorkstationStore();

  const currentView = (searchParams.get('view') as 'scan' | 'invoice' | 'submit') || 'scan';

  // Sync URL with store on mount and changes
  useEffect(() => {
    if (orderId) {
      setOrderId(orderId);
    }
    if (currentView !== activeView) {
      setActiveView(currentView);
    }
  }, [orderId, currentView, activeView, setOrderId, setActiveView]);

  // Fetch order details
  const { data: order, isLoading: orderLoading, error: orderError } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();
      
      if (error) throw error;
      
      return {
        id: data.id,
        supplierName: data.supplier_name,
        deliveryDate: data.delivery_date,
        status: data.status as "active" | "completed" | "archived",
        productCount: data.product_count,
        hasCredit: data.has_credit,
        documents: data.documents || []
      };
    },
    enabled: !!orderId
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['master-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('master_categories')
        .select('id, category_name, square_category_id, is_active')
        .eq('is_active', true)
        .order('category_name');
      
      if (error) throw error;
      
      return data?.map(cat => ({ 
        id: cat.id, 
        name: cat.category_name,
        square_category_id: cat.square_category_id 
      })) || [];
    }
  });

  // Fetch products using optimized hook
  const { 
    products: optimizedProducts, 
    productsLoading, 
    productsError,
    totalProducts,
    isLargeDataset,
    loadingProgress
  } = useOptimizedProductList(orderId || '', true);

  // Fetch invoice items
  const { data: invoiceItemsData = [] } = useQuery({
    queryKey: ['invoice-items', orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_items')
        .select(`
          *,
          invoice_documents!inner(order_id)
        `)
        .eq('invoice_documents.order_id', orderId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!orderId
  });

  // Transform and sync data to store
  useEffect(() => {
    if (categories.length > 0) {
      setCategories(categories);
    }
  }, [categories, setCategories]);

  useEffect(() => {
    if (optimizedProducts && optimizedProducts.length > 0) {
      const transformedProducts: Product[] = optimizedProducts.map(product => ({
        id: product.id,
        barcode: product.barcode,
        name: product.name,
        boxes: product.boxes,
        unitsPerBox: product.unitsPerBox,
        photos: product.photos || [],
        
        // Pricing - use safe access with fallbacks
        costPerUnit: product.costPerUnit,
        costPerBox: product.costPerUnit && product.unitsPerBox ? product.costPerUnit * product.unitsPerBox : undefined,
        suggestedRetailPrice: product.suggestedRetailPrice,
        retailMultiplier: 1.5, // Default multiplier
        
        // Categorization - auto-fill for existing POS products with mapped category
        categoryId: product.posStatus === 'found' && product.mappedCategoryId ? product.mappedCategoryId : undefined,
        categoryName: product.posStatus === 'found' && product.mappedCategoryId ? 
          categories.find(cat => cat.id === product.mappedCategoryId)?.name : undefined,
        aiSuggestedCategory: undefined,
        
        // Status
        posStatus: product.posStatus || 'not-found',
        submitStatus: 'draft',
        syncStatus: 'pending',
        
        // Metadata - calculate data completeness
        dataQuality: calculateDataQuality(product),
        hasCredit: product.hasCredit || false,
        lastModifiedBy: undefined,
        createdAt: product.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        
        // Square Integration
        square_item_id: product.square_item_id,
        square_variation_id: product.square_variation_id,
        
        // Master Product Data
        masterProduct: product.catalogName ? {
          id: '',
          productName: product.catalogName,
          retailPrice: product.suggestedRetailPrice,
          productCost: product.costPerUnit,
          dataSource: 'catalog'
        } : undefined,
        
        // AI Enhancement
        aiEnhancedName: undefined
      }));
      
      setProducts(transformedProducts);
    }
  }, [optimizedProducts, setProducts]);

  useEffect(() => {
    if (invoiceItemsData.length > 0) {
      const transformedInvoiceItems = invoiceItemsData.map(item => ({
        ...item,
        validation_status: (item.validation_status as 'pending' | 'approved' | 'rejected') || 'pending'
      }));
      setInvoiceItems(transformedInvoiceItems);
    }
  }, [invoiceItemsData, setInvoiceItems]);

  // Update loading state
  useEffect(() => {
    setLoading(orderLoading || productsLoading);
  }, [orderLoading, productsLoading, setLoading]);

  // Handle errors
  if (productsError && (productsError as any).code === '57014') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Database Timeout</h2>
          <p className="text-gray-600 mb-4">
            This order has too many products to load quickly. Please try refreshing.
          </p>
          <div className="space-y-2">
            <Button onClick={() => window.location.reload()} className="w-full">
              Refresh Page
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="w-full">
              Back to Orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (orderLoading || productsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <Package className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {orderLoading ? 'Loading Order...' : 'Loading Products...'}
          </h2>
          {productsLoading && loadingProgress.total > 0 && (
            <div className="space-y-2">
              <p className="text-gray-600">
                Processing {loadingProgress.loaded} of {loadingProgress.total} products
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-4">
            The order you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  // Handle archived orders
  if (order.status === 'archived') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <Package className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order Completed</h2>
          <p className="text-gray-600 mb-4">
            This order from <strong>{order.supplierName}</strong> has been submitted and archived.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'scan':
        return <IncomingScanView />;
      case 'invoice':
        return <InvoiceMatchingView />;
      case 'submit':
        return <SubmissionConsole />;
      default:
        return <IncomingScanView />;
    }
  };

  return (
    <AuthGuard>
      <AutoMatchProvider>
        <WorkstationLayout
          orderId={orderId!}
          orderInfo={{
            supplierName: order.supplierName,
            deliveryDate: order.deliveryDate,
            status: order.status
          }}
        >
          {/* Large dataset warning */}
          {isLargeDataset && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 dark:bg-yellow-900/20 dark:border-yellow-800">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Large Dataset Detected</h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                    This order has {totalProducts} products. Performance optimizations are active.
                  </p>
                </div>
              </div>
            </div>
          )}

          {renderCurrentView()}
        </WorkstationLayout>
      </AutoMatchProvider>
    </AuthGuard>
  );
};

export default Workstation;